import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useMyReviewFor, useUpsertReview } from "@/hooks/use-reviews";
import { cn } from "@/lib/utils";

interface ReviewDialogProps {
  teacherUserId: string;
  teacherName: string;
  /** Controlled open state. When omitted, the dialog manages its own open state
   *  and shows its default trigger button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger (used when an external affordance opens the dialog). */
  hideTrigger?: boolean;
}

const STAR_VALUES = [1, 2, 3, 4, 5];
const MIN_COMMENT_LENGTH = 10;
const MAX_COMMENT_LENGTH = 500;

/** Maps the raw `Error` thrown by `upsertReview` (message ends with the HTTP
 * status, e.g. "Failed to submit review: 403") to a user-facing message. */
function getErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("429")) return "You can only edit your review once per day — please wait and try again later.";
  if (message.includes("403")) return "You can only review teachers you've contacted.";
  if (message.includes("400")) return "Please check your rating and review text.";
  return "Something went wrong. Please try again.";
}

export function ReviewDialog({
  teacherUserId,
  teacherName,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: ReviewDialogProps) {
  const { data: myReview } = useMyReviewFor(teacherUserId);
  const upsertReview = useUpsertReview(teacherUserId);

  const [internalOpen, setInternalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedStatus, setSubmittedStatus] = useState<string | null>(null);
  const starRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };

  const existingReview = myReview?.review ?? null;
  const isEditMode = existingReview != null;

  // Prefill on open — edit mode pulls the existing rating/comment, create mode starts blank.
  useEffect(() => {
    if (open) {
      setRating(existingReview?.rating ?? 0);
      setComment(existingReview?.comment ?? "");
      setHoveredRating(0);
      setErrorMessage(null);
      setSubmittedStatus(null);
    }
  }, [open, existingReview]);

  const displayRating = hoveredRating || rating;
  const isCommentValid = comment.trim().length >= MIN_COMMENT_LENGTH && comment.length <= MAX_COMMENT_LENGTH;
  const canSubmit = rating > 0 && isCommentValid && !upsertReview.isPending;

  const handleStarKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = rating || 1;
    let next = current;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      next = current >= 5 ? 1 : current + 1;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      next = current <= 1 ? 5 : current - 1;
    } else {
      return;
    }
    e.preventDefault();
    setRating(next);
    starRefs.current[next - 1]?.focus();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setErrorMessage(null);
    try {
      const result = await upsertReview.mutateAsync({ rating, comment: comment.trim() });
      // A newly submitted/edited review is PENDING moderation — confirm it is under
      // review rather than closing silently (which would imply it is already live).
      setSubmittedStatus(result.status ?? "PENDING");
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs gap-1 cursor-pointer transition-colors duration-200"
          >
            <Star className="w-3.5 h-3.5" />
            {isEditMode ? "Edit your review" : "Write a review"}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit your review" : "Write a review"}</DialogTitle>
          <DialogDescription>Share your experience with {teacherName}.</DialogDescription>
        </DialogHeader>

        {submittedStatus == null && existingReview?.status === "REMOVED" && (
          // The author is re-opening a review that moderation removed — show the
          // sanitized reason (ADR-125) so they know what to fix before resubmitting.
          <p role="alert" className="text-sm text-destructive">
            Your previous review was removed by moderation
            {existingReview.moderationReason ? `: ${existingReview.moderationReason}` : "."}
            {existingReview.moderationReason ? "." : ""} Please revise it below.
          </p>
        )}

        {submittedStatus != null ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Thanks! Your review has been submitted and is now under review. It will
              appear once our team has checked it.
            </p>
            <DialogFooter>
              <Button
                onClick={() => setOpen(false)}
                className="cursor-pointer transition-colors duration-200"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
        <>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Your Rating
            </Label>
            <div
              role="radiogroup"
              aria-label="Your rating"
              className="flex items-center gap-1"
              onKeyDown={handleStarKeyDown}
            >
              {STAR_VALUES.map((star) => (
                <button
                  key={star}
                  ref={(el) => { starRefs.current[star - 1] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={rating === star}
                  aria-label={`${star} star${star === 1 ? "" : "s"}`}
                  tabIndex={(rating || 1) === star ? 0 : -1}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 rounded cursor-pointer transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Star
                    className={cn(
                      "w-8 h-8 transition-colors duration-150",
                      star <= displayRating ? "text-warning fill-warning" : "text-border"
                    )}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="review-comment" className="text-xs uppercase tracking-wider text-muted-foreground">
                Comment
              </Label>
              <span
                className={cn(
                  "text-xs transition-colors",
                  comment.trim().length >= MIN_COMMENT_LENGTH ? "text-primary" : "text-muted-foreground"
                )}
              >
                {comment.length}/{MAX_COMMENT_LENGTH}
              </span>
            </div>
            <Textarea
              id="review-comment"
              placeholder={`What was your experience learning with ${teacherName}?`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={MAX_COMMENT_LENGTH}
              className="resize-none"
            />
          </div>

          {errorMessage && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="cursor-pointer transition-colors duration-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="cursor-pointer transition-colors duration-200"
          >
            {upsertReview.isPending ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
